import { useMemo } from "react";
import { metresToFeet, msToKnots, prettyCallsign } from "../../lib/format.js";
import "./Leaderboard.css";

function Row({ label, icao24, callsign, value, valueLabel, onSelect }) {
  const hasWinner = icao24 != null;
  return (
    <li className="lb-row" role="listitem">
      <span className="lb-label">{label}</span>
      {hasWinner ? (
        <button
          type="button"
          className="lb-name"
          onClick={() => onSelect?.(icao24)}
          aria-label={`${label}: ${callsign}, ${valueLabel}. Select aircraft.`}
        >
          {callsign}
        </button>
      ) : (
        <span className="lb-name lb-name-empty" aria-hidden="true">
          —
        </span>
      )}
      <span className="lb-value" aria-hidden="true">
        {value}
      </span>
    </li>
  );
}

export default function Leaderboard({ aircraft, onSelect }) {
  const records = useMemo(() => {
    const list = Array.isArray(aircraft) ? aircraft : [];

    let highest = null;
    let fastest = null;
    const typeCounts = new Map();

    for (const a of list) {
      if (
        a.baroAltitude != null &&
        !a.onGround &&
        (highest == null || a.baroAltitude > highest.baroAltitude)
      ) {
        highest = a;
      }

      if (
        a.velocity != null &&
        (fastest == null || a.velocity > fastest.velocity)
      ) {
        fastest = a;
      }

      if (a.type) {
        typeCounts.set(a.type, (typeCounts.get(a.type) || 0) + 1);
      }
    }

    let topType = null;
    let topTypeCount = 0;
    for (const [type, count] of typeCounts) {
      if (count > topTypeCount) {
        topType = type;
        topTypeCount = count;
      }
    }

    return { highest, fastest, topType, topTypeCount };
  }, [aircraft]);

  const { highest, fastest, topType, topTypeCount } = records;

  const highestFt = highest ? metresToFeet(highest.baroAltitude) : null;
  const highestValue =
    highestFt != null ? `${highestFt.toLocaleString()} ft` : "—";

  const fastestKn = fastest ? msToKnots(fastest.velocity) : null;
  const fastestValue = fastestKn != null ? `${fastestKn} kn` : "—";

  const typeValue = topType
    ? `${topTypeCount.toLocaleString()} ${topTypeCount === 1 ? "aircraft" : "aircraft"}`
    : "—";

  return (
    <section className="leaderboard" aria-label="Live records right now">
      <h2 className="lb-title">Records right now</h2>
      <ul className="lb-list" role="list">
        <Row
          label="Highest"
          icao24={highest?.icao24}
          callsign={highest ? prettyCallsign(highest.callsign) : null}
          value={highestValue}
          valueLabel={highestValue}
          onSelect={onSelect}
        />
        <Row
          label="Fastest"
          icao24={fastest?.icao24}
          callsign={fastest ? prettyCallsign(fastest.callsign) : null}
          value={fastestValue}
          valueLabel={fastestValue}
          onSelect={onSelect}
        />
        <li className="lb-row" role="listitem">
          <span className="lb-label">Most common</span>
          <span className="lb-name lb-name-static">
            {topType || <span className="lb-name-empty" aria-hidden="true">—</span>}
          </span>
          <span className="lb-value" aria-hidden="true">
            {typeValue}
          </span>
        </li>
      </ul>
    </section>
  );
}
